import React, { useState, useRef } from 'react';

const PRESET_TYPES = ['Recruiter', 'Talent Acquisition', 'HR', 'Hiring Manager', 'Engineering Manager', 'Professor', 'Professor Emeritus', 'Advisor', 'Interviewer', 'Employee', 'Referral', 'Coworker', 'Alumni', 'Other'];
const CONTACT_STATUSES = ['Not Contacted', 'Emailed', 'Awaiting Reply', 'Asked Referral', 'Referral Given', 'In Touch', 'Ghosted'];
const BLANK = { name: '', title: '', company: '', email: '', phone: '', linkedin_url: '', type: 'Recruiter', notes: '', application_id: '', contact_status: 'Not Contacted' };

const toStr = (v) => (v == null ? '' : String(v));

const CONTACT_COLS_KEY = 'applyai_contact_cols';
function loadContactCols() { try { return JSON.parse(localStorage.getItem(CONTACT_COLS_KEY) || '[]'); } catch { return []; } }
function saveContactCols(c) { localStorage.setItem(CONTACT_COLS_KEY, JSON.stringify(c)); }

const STATUS_COLORS = {
  'Not Contacted': '#94a3b8',
  'Emailed': '#3b82f6',
  'Awaiting Reply': '#f59e0b',
  'Asked Referral': '#8b5cf6',
  'Referral Given': '#10b981',
  'In Touch': '#06b6d4',
  'Ghosted': '#ef4444',
};

// Select with local state — prevents brief revert while React re-renders
function LocalSelect({ value, options, onChange, className, style }) {
  const [val, setVal] = React.useState(value || '');
  React.useEffect(() => setVal(value || ''), [value]);
  return (
    <select
      value={val}
      className={className}
      style={style}
      onChange={e => { setVal(e.target.value); onChange(e.target.value); }}
    >
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

// Input with local state — lets user type freely; saves only on blur/Enter
function LocalInput({ value, onSave, list, className }) {
  const [val, setVal] = React.useState(value || '');
  React.useEffect(() => setVal(value || ''), [value]);
  return (
    <input
      list={list}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => { if (val !== (value || '')) onSave(val); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setVal(value || ''); }}
      className={className}
    />
  );
}

function InlineEdit({ value, placeholder, onSave, className = '', multiline = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const start = () => { setVal(value || ''); setEditing(true); };
  const commit = () => { setEditing(false); if (val !== (value || '')) onSave(val); };
  const onKey = (e) => {
    if (e.key === 'Enter' && !multiline) commit();
    if (e.key === 'Escape') { setVal(value || ''); setEditing(false); }
  };
  if (editing) {
    return multiline
      ? <textarea autoFocus rows={2} value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={onKey} className="contact-inline-input" style={{ resize: 'none' }} />
      : <input autoFocus value={val} onChange={e => setVal(e.target.value)} onBlur={commit} onKeyDown={onKey} className="contact-inline-input" />;
  }
  return (
    <span className={`editable-field ${className}`} onClick={start} title="Click to edit">
      {value || <span style={{ color: '#cbd5e1' }}>{placeholder}</span>}
    </span>
  );
}

function ContactModal({ contact, applications, onSave, onClose }) {
  const [form, setForm] = useState(contact ? {
    name: toStr(contact.name), title: toStr(contact.title), company: toStr(contact.company),
    email: toStr(contact.email), phone: toStr(contact.phone), linkedin_url: toStr(contact.linkedin_url),
    type: toStr(contact.type) || 'Recruiter', notes: toStr(contact.notes),
    application_id: toStr(contact.application_id), contact_status: toStr(contact.contact_status) || 'Not Contacted',
  } : BLANK);
  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{contact ? 'Edit Contact' : 'Add Contact'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="modal-form">
          <div className="form-row">
            <div className="form-group"><label>Name *</label><input name="name" value={form.name} onChange={set} required placeholder="Jane Smith" /></div>
            <div className="form-group">
              <label>Type <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>(pick or type your own)</span></label>
              <input name="type" value={form.type} onChange={set} list="contact-types-list" placeholder="Recruiter" />
              <datalist id="contact-types-list">{PRESET_TYPES.map(t => <option key={t} value={t} />)}</datalist>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input name="title" value={form.title} onChange={set} placeholder="Technical Recruiter" /></div>
            <div className="form-group"><label>Company</label><input name="company" value={form.company} onChange={set} placeholder="Google" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Email</label><input name="email" type="email" value={form.email} onChange={set} placeholder="jane@company.com" /></div>
            <div className="form-group"><label>Phone</label><input name="phone" value={form.phone} onChange={set} placeholder="+1 (555) 000-0000" /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Outreach Status</label>
              <select name="contact_status" value={form.contact_status} onChange={set}>
                {CONTACT_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Linked Application</label>
              <select name="application_id" value={form.application_id} onChange={set}>
                <option value="">None</option>
                {applications.map(a => <option key={a.id} value={a.id}>{a.company} — {a.role}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>Profile / Website URL</label><input name="linkedin_url" value={form.linkedin_url} onChange={set} placeholder="https://linkedin.com/in/… or any URL" /></div>
          <div className="form-group"><label>Notes</label><textarea name="notes" value={form.notes} onChange={set} rows={3} placeholder="How you met, follow-up notes..." /></div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">{contact ? 'Save' : 'Add Contact'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ContactRowExpand({ contact, onPatch }) {
  const [notes, setNotes] = useState(contact.notes || '');
  const [saved, setSaved] = useState(false);
  const timer = useRef(null);

  React.useEffect(() => { setNotes(contact.notes || ''); }, [contact.notes]);

  const handleChange = (val) => {
    setNotes(val);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      onPatch(contact.id, { notes: val });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }, 800);
  };

  return (
    <tr className="row-expand-tr">
      <td colSpan={999} className="row-expand-td">
        <div className="row-expand-body contact-row-expand-body">
          <div className="row-expand-col" style={{ maxWidth: '640px' }}>
            <div className="row-expand-col-title">
              Notes — used by AI email assistant
              {saved && <span className="row-expand-saved">✓ Saved</span>}
            </div>
            <textarea
              className="row-expand-notes"
              value={notes}
              onChange={e => handleChange(e.target.value)}
              placeholder="How you met, conversation history, what to follow up on… The AI email assistant uses these notes when drafting outreach."
              rows={4}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function ContactsPage({ contacts, applications, API_URL, onUpdate, onViewJob, openContactId, onContactOpened }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');

  // Auto-open contact dialog when navigated from job tracker
  React.useEffect(() => {
    if (openContactId && contacts.length > 0) {
      const c = contacts.find(x => x.id === openContactId);
      if (c) { setEditing(c); setModalOpen(true); }
      if (onContactOpened) onContactOpened();
    }
  }, [openContactId, contacts]); // eslint-disable-line
  const [customCols, setCustomCols] = useState(loadContactCols);
  const [addingCol, setAddingCol] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const filtered = contacts
    .filter(c => {
      const q = search.toLowerCase();
      return !q || (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const getVal = (c) => String(c[sortKey] ?? '').toLowerCase();
      let av = getVal(a);
      let bv = getVal(b);
      if (sortKey === 'contact_status') { av = STATUS_COLORS[a.contact_status] || ''; bv = STATUS_COLORS[b.contact_status] || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const patch = async (id, fields) => {
    const contact = contacts.find(c => c.id === id);
    if (!contact) return;

    // Build optimistic state immediately so inputs don't revert
    const optimistic = { ...contact, ...fields };
    if ('application_id' in fields) {
      if (!fields.application_id) {
        optimistic.app_company = null;
        optimistic.app_role = null;
      } else {
        const app = applications.find(a => String(a.id) === String(fields.application_id));
        if (app) { optimistic.app_company = app.company; optimistic.app_role = app.role; }
      }
    }
    onUpdate(contacts.map(c => c.id === id ? optimistic : c));

    try {
      const res = await fetch(`${API_URL}/api/contacts/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contact, ...fields }),
      });
      if (!res.ok) { console.error('Contact update failed:', res.status); return; }
      const saved = await res.json();
      onUpdate(contacts.map(c => c.id === id ? {
        ...c, ...saved,
        app_company: saved.app_company ?? optimistic.app_company,
        app_role: saved.app_role ?? optimistic.app_role,
      } : c));
    } catch (err) {
      console.error('Contact patch error:', err);
    }
  };

  const patchCustom = (id, colId, value) => {
    const contact = contacts.find(c => c.id === id);
    const current = contact?.custom_fields || {};
    patch(id, { custom_fields: { ...current, [colId]: value } });
  };

  const handleSave = async (form) => {
    if (editing) {
      const res = await fetch(`${API_URL}/api/contacts/${editing.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { console.error('Contact save failed:', await res.text()); return; }
      const updated = await res.json();
      onUpdate(contacts.map(c => c.id === editing.id ? updated : c));
    } else {
      const res = await fetch(`${API_URL}/api/contacts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { console.error('Contact create failed:', await res.text()); return; }
      onUpdate([await res.json(), ...contacts]);
    }
    setModalOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await fetch(`${API_URL}/api/contacts/${id}`, { method: 'DELETE' });
    onUpdate(contacts.filter(c => c.id !== id));
  };

  const addCustomCol = () => {
    const name = newColName.trim();
    if (!name) return;
    const col = { id: `cc_${Date.now()}`, label: name };
    const updated = [...customCols, col];
    setCustomCols(updated); saveContactCols(updated);
    setNewColName(''); setAddingCol(false);
  };

  const removeCustomCol = (id) => {
    const updated = customCols.filter(c => c.id !== id);
    setCustomCols(updated); saveContactCols(updated);
  };

  const statusDot = (status) => {
    const color = STATUS_COLORS[status] || '#94a3b8';
    return <span className="contact-status-dot" style={{ background: color }} title={status} />;
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2 className="page-title">Contacts</h2>
          <p className="page-sub">{contacts.length} contact{contacts.length !== 1 ? 's' : ''} · Click any field to edit inline</p>
        </div>
        <div className="header-actions">
          <input className="search-input" placeholder="🔍  Search contacts..." value={search} onChange={e => setSearch(e.target.value)} />
          <div className="view-toggle">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List</button>
            <button className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')}>Cards</button>
          </div>
          <button className="btn-primary" onClick={() => { setEditing(null); setModalOpen(true); }}>+ Add Contact</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <h3>No contacts yet</h3>
          <p>Save recruiters, hiring managers, and referrals here.</p>
        </div>
      ) : viewMode === 'list' ? (
        <div className="contacts-list-wrapper">
          <table className="contacts-table">
            <colgroup>
              <col style={{ width: '150px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '105px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '175px' }} />
              <col style={{ width: '120px' }} />
              <col style={{ width: '110px' }} />
              {customCols.map(col => <col key={col.id} style={{ width: '110px' }} />)}
              <col style={{ width: '110px' }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Name{sortArrow('name')}</th>
                <th onClick={() => toggleSort('contact_status')} style={{ cursor: 'pointer' }}>Status{sortArrow('contact_status')}</th>
                <th onClick={() => toggleSort('type')} style={{ cursor: 'pointer' }}>Type{sortArrow('type')}</th>
                <th onClick={() => toggleSort('company')} style={{ cursor: 'pointer' }}>Company{sortArrow('company')}</th>
                <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer' }}>Email{sortArrow('email')}</th>
                <th>Phone</th>
                <th onClick={() => toggleSort('app_company')} style={{ cursor: 'pointer' }}>Linked Job{sortArrow('app_company')}</th>
                {customCols.map(col => (
                  <th key={col.id} style={{ position: 'relative' }}>
                    {col.label}
                    <button className="col-remove-btn" onClick={() => removeCustomCol(col.id)} title="Remove column">×</button>
                  </th>
                ))}
                <th>
                  {addingCol ? (
                    <div className="add-col-input-row" onClick={e => e.stopPropagation()}>
                      <input autoFocus value={newColName} onChange={e => setNewColName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addCustomCol(); if (e.key === 'Escape') setAddingCol(false); }}
                        placeholder="Column name" className="add-col-input" />
                      <button className="add-col-ok" onClick={addCustomCol}>✓</button>
                      <button className="add-col-cancel" onClick={() => setAddingCol(false)}>✕</button>
                    </div>
                  ) : (
                    <button className="add-col-btn" onClick={() => setAddingCol(true)}>+ Add Column</button>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <React.Fragment key={c.id}>
                <tr className={expandedContactId === c.id ? 'row-expanded' : ''}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="contact-avatar-sm">{(c.name || '?').charAt(0).toUpperCase()}</div>
                      <InlineEdit value={c.name} placeholder="Name" className="contact-name-sm" onSave={val => patch(c.id, { name: val })} />
                    </div>
                  </td>
                  <td style={{ padding: '0' }}>
                    <LocalSelect
                      className="contact-status-select"
                      value={c.contact_status || 'Not Contacted'}
                      options={CONTACT_STATUSES}
                      style={{ '--dot-color': STATUS_COLORS[c.contact_status] || '#94a3b8' }}
                      onChange={v => patch(c.id, { contact_status: v })}
                    />
                  </td>
                  <td>
                    <LocalInput
                      value={c.type || ''}
                      list={`types-${c.id}`}
                      className="contact-type-input editable-field"
                      onSave={val => patch(c.id, { type: val })}
                    />
                    <datalist id={`types-${c.id}`}>{PRESET_TYPES.map(t => <option key={t} value={t} />)}</datalist>
                  </td>
                  <td>
                    <InlineEdit value={c.company} placeholder="—" onSave={val => patch(c.id, { company: val })} />
                  </td>
                  <td>
                    <InlineEdit value={c.email} placeholder="—" onSave={val => patch(c.id, { email: val })} />
                  </td>
                  <td>
                    <InlineEdit value={c.phone} placeholder="—" onSave={val => patch(c.id, { phone: val })} />
                  </td>
                  <td>
                    {c.app_company ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                          className="linked-job-btn"
                          onClick={() => onViewJob && onViewJob(c.application_id)}
                          title={`${c.app_company} — ${c.app_role}`}
                        >
                          {c.app_company}
                          <span className="linked-job-arrow">↗</span>
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => patch(c.id, { application_id: null })}
                          title="Unlink job"
                          style={{ color: '#ef4444', fontSize: '0.7rem', padding: '2px 5px' }}
                        >✕</button>
                      </div>
                    ) : (
                      <select
                        className="link-job-select"
                        value={c.application_id || ''}
                        onChange={e => patch(c.id, { application_id: e.target.value || null })}
                      >
                        <option value="">Link…</option>
                        {applications.map(a => <option key={a.id} value={a.id}>{a.company} — {a.role}</option>)}
                      </select>
                    )}
                  </td>
                  {customCols.map(col => {
                    const val = (c.custom_fields || {})[col.id] || '';
                    return (
                      <td key={col.id}>
                        <InlineEdit value={val} placeholder="—" onSave={v => patchCustom(c.id, col.id, v)} />
                      </td>
                    );
                  })}
                  <td onClick={e => e.stopPropagation()} style={{ padding: '6px 8px' }}>
                    <div style={{ display: 'flex', gap: '1px', alignItems: 'center' }}>
                      <button
                        className={`btn-icon view-btn ${expandedContactId === c.id ? 'view-btn-active' : ''}`}
                        onClick={() => setExpandedContactId(id => id === c.id ? null : c.id)}
                        title={expandedContactId === c.id ? 'Collapse notes' : 'Expand notes'}
                      >
                        {expandedContactId === c.id ? '▲' : '▼'}
                      </button>
                      {c.email && <a href={`mailto:${c.email}`} className="btn-icon" title="Send email" style={{ textDecoration: 'none' }}>✉️</a>}
                      <button className="btn-icon" onClick={() => { setEditing(c); setModalOpen(true); }} title="Full edit">✏️</button>
                      <button className="btn-icon" onClick={() => handleDelete(c.id)} title="Delete">🗑️</button>
                    </div>
                  </td>
                </tr>
                {expandedContactId === c.id && (
                  <ContactRowExpand contact={c} onPatch={patch} />
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="contacts-grid">
          {filtered.map(c => (
            <div key={c.id} className="contact-card">
              <div className="contact-card-header">
                <div className="contact-avatar-lg">{(c.name || '?').charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <InlineEdit value={c.name} placeholder="Name" className="contact-name" onSave={val => patch(c.id, { name: val })} />
                  <div className="contact-title-co" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <InlineEdit value={c.title} placeholder="Title" onSave={val => patch(c.id, { title: val })} />
                    {(c.title && c.company) && <span style={{ color: '#cbd5e1' }}>·</span>}
                    <InlineEdit value={c.company} placeholder="Company" onSave={val => patch(c.id, { company: val })} />
                  </div>
                </div>
                {statusDot(c.contact_status)}
              </div>
              <span className="contact-type-badge">{c.type}</span>
              <div className="contact-detail">✉️ <InlineEdit value={c.email} placeholder="Add email" onSave={val => patch(c.id, { email: val })} /></div>
              <div className="contact-detail">📞 <InlineEdit value={c.phone} placeholder="Add phone" onSave={val => patch(c.id, { phone: val })} /></div>
              {c.linkedin_url && <div className="contact-detail"><a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{ color: '#0ea5e9', textDecoration: 'none' }}>🔗 Profile</a></div>}
              {c.app_company && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                  <button className="linked-job-btn" onClick={() => onViewJob && onViewJob(c.application_id)}>
                    📎 {c.app_company} — {c.app_role} <span className="linked-job-arrow">↗</span>
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => patch(c.id, { application_id: null })}
                    title="Unlink job"
                    style={{ color: '#ef4444', fontSize: '0.7rem', padding: '2px 5px' }}
                  >✕</button>
                </div>
              )}
              <div className="contact-actions">
                {c.email && <a href={`mailto:${c.email}`} className="btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 10px', textDecoration: 'none' }}>Email</a>}
                <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '5px 10px' }} onClick={() => { setEditing(c); setModalOpen(true); }}>Full Edit</button>
                <button className="btn-danger" style={{ fontSize: '0.78rem', padding: '5px 10px' }} onClick={() => handleDelete(c.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <ContactModal contact={editing} applications={applications} onSave={handleSave} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}
